/**
 * Privacy Policy Content for Eskerium Platform
 * Terminal-formatted GDPR-compliant privacy policy content
 * 
 * @version 2025.1.0
 * @updated 2025-01-01
 * @compliance GDPR, CPRA
 */

export const PRIVACY_POLICY_CONTENT = `PRIVACY POLICY v2025.1.0
Eskerium Biosciences LLC

> GDPR_COMPLIANT=true
> LAST_UPDATED=2025-01-01
> JURISDICTION=EU|US|UK
> STATUS=ACTIVE

================================================================================
[01] DATA CONTROLLER IDENTIFICATION
================================================================================

ORGANIZATION: Eskerium LLC
LEGAL_ENTITY: Eskerium Limited Liability Company
ADDRESS: [To be provided by Eskerium]
DPO_CONTACT: privacy@eskerium.com
DPO_PHONE: [To be provided by Eskerium]

> DATA PROTECTION OFFICER CERTIFICATION: ISO27001/GDPR_CERTIFIED

================================================================================
[02] LAWFUL BASIS FOR PROCESSING
================================================================================

HEALTH SUPPLEMENT DATA PROCESSING
─────────────────────────────────────────────────────────────────────────────

[✓] CONSENT (Art. 6(1)(a) GDPR) - Explicit consent for health questionnaires
[✓] CONTRACT (Art. 6(1)(b) GDPR) - Order processing and fulfillment
[✓] LEGAL_OBLIGATION (Art. 6(1)(c)) - Tax records, health regulations
[✓] VITAL_INTERESTS (Art. 6(1)(d)) - Emergency contact, allergy alerts
[✓] LEGITIMATE_INTEREST (Art. 6(1)(f)) - Fraud prevention, analytics

⚠️  SPECIAL CATEGORY DATA (Art. 9 GDPR):
    Health-related information requires EXPLICIT CONSENT only.
    Consent can be withdrawn at any time via user dashboard.

================================================================================
[03] YOUR RIGHTS UNDER GDPR (Art. 12-22)
================================================================================

> ACCESS         │ Request copy of your personal data
> RECTIFICATION  │ Correct inaccurate information
> ERASURE        │ "Right to be forgotten"
> PORTABILITY    │ Transfer data to another service
> RESTRICTION    │ Limit processing of your data
> OBJECTION      │ Object to processing for direct marketing
> WITHDRAW       │ Remove consent (health data)
> COMPLAINT      │ Lodge complaint with supervisory authority

HOW TO EXERCISE YOUR RIGHTS
─────────────────────────────────────────────────────────────────────────────

EMAIL: privacy@eskerium.com
PORTAL: Account Settings > Privacy Controls
RESPONSE TIME: 30 days maximum (may extend to 60 days for complex requests)

================================================================================
[04] DATA COLLECTION & USAGE
================================================================================

ACCOUNT DATA
─────────────────────────────────────────────────────────────────────────────
• Email address              │ Legal basis: Contract + Consent
• Full name                  │ Retention: 7 years post-account
• Billing/shipping address   │ Purpose: Order fulfillment
• Phone number               │ Sharing: Payment processor only

HEALTH & WELLNESS DATA
─────────────────────────────────────────────────────────────────────────────
• Health questionnaire responses    │ Legal basis: Explicit consent
• Dietary preferences/restrictions  │ Retention: Until consent withdrawn
• Supplement usage history          │ Purpose: Personalized recommendations
• Allergy information               │ Sharing: None (processed internally)

================================================================================
[05] DATA SECURITY MEASURES (Art. 32 GDPR)
================================================================================

TECHNICAL SAFEGUARDS
─────────────────────────────────────────────────────────────────────────────

[✓] TLS 1.3 ENCRYPTION in transit
[✓] AES-256 ENCRYPTION at rest
[✓] MULTI-FACTOR AUTHENTICATION for admin access
[✓] REGULAR SECURITY AUDITS (quarterly)
[✓] PENETRATION TESTING (annually)
[✓] BACKUP ENCRYPTION with 3-2-1 strategy

================================================================================
[06] COOKIES & TRACKING
================================================================================

>>> COOKIE_POLICY = {
      "STRICTLY_NECESSARY": {
        "purpose": "Essential site functionality",
        "consent_required": false,
        "examples": "Session ID, security tokens"
      },
      "ANALYTICS": {
        "purpose": "Site performance measurement",
        "consent_required": true,
        "provider": "Google Analytics (anonymized)"
      },
      "FUNCTIONAL": {
        "purpose": "Remember preferences",
        "consent_required": true,
        "examples": "Language, currency, cart contents"
      }
    }

> COOKIE_BANNER: Granular consent options provided
> WITHDRAW_CONSENT: Available in Privacy Settings

================================================================================
[07] INTERNATIONAL DATA TRANSFERS
================================================================================

TRANSFER MECHANISMS
─────────────────────────────────────────────────────────────────────────────

EU → US: STANDARD CONTRACTUAL CLAUSES (2021 version)
EU → UK: ADEQUACY DECISION (until further notice)
PROCESSORS: GDPR Article 28 compliant agreements

================================================================================
[08] CALIFORNIA PRIVACY RIGHTS (CPRA 2025)
================================================================================

For California residents, additional rights apply:

CPRA CONSUMER RIGHTS
─────────────────────────────────────────────────────────────────────────────
[✓] Right to KNOW what personal information is collected
[✓] Right to DELETE personal information
[✓] Right to CORRECT inaccurate personal information
[✓] Right to OPT-OUT of sale/sharing of personal information
[✓] Right to LIMIT use of sensitive personal information
[✓] Right to NON-DISCRIMINATION for exercising privacy rights

⚠️  WE DO NOT SELL YOUR PERSONAL INFORMATION

================================================================================
[09] CONTACT INFORMATION
================================================================================

DATA PROTECTION INQUIRIES
─────────────────────────────────────────────────────────────────────────────

EMAIL: privacy@eskerium.com
WEBSITE: https://eskerium.com/privacy

SUPERVISORY AUTHORITY:
You have the right to lodge a complaint with your local
data protection authority.

================================================================================

LAST UPDATED: 2025-01-01 00:00:00 UTC
VERSION: v2025.1.0
EFFECTIVE: Immediately upon publication

> END_OF_POLICY
> SYSTEM_EXIT: Return to main terminal`;